import { NavLink, Link } from 'react-router-dom';
import {
  Home,
  Users,
  Wallet,
  Bell,
  User,
  Receipt,
  PiggyBank,
  Upload,
  CreditCard,
  Building2,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useCurrentUser } from '../../../application/hooks/useCurrentUser';
import { useNotices } from '../../../application/hooks/useNotices';
import { useDashboardStats } from '../../../application/hooks/useDashboard';
import {
  TierLevel,
  TIER_RANK_MAP,
  TIER_DESIGNATION_MAP,
} from '../../../domain/enums/TierLevel';
import './Sidebar.css';
import { BKASH_ENABLED, PAY_FALLBACK_PATH } from '../../../config/features';
import { resolveWallet } from '../../../domain/calculators/processWithdrawal';
import { PayLink } from '../payments/PayLink';
import { usePaymentLock } from '../../../application/hooks/usePaymentLock';

interface SidebarEntry {
  to: string;
  labelKey: string;
  icon: typeof Home;
  minTier?: TierLevel;
}

/** BD-31: subscribers (L1) and agents (L2–L6) are separate accounts, so each sees only its own hub. */
type Audience = 'SUBSCRIBER' | 'AGENT' | 'ALL';

interface NavSection {
  title: string;
  audience: Audience;
  entries: SidebarEntry[];
}

const SECTIONS: NavSection[] = [
  {
    title: 'Owner Hub',
    audience: 'SUBSCRIBER',
    entries: [
      { to: '/', labelKey: 'nav.dashboard', icon: Home },
      { to: '/installments', labelKey: 'nav.installments', icon: Receipt },
      { to: '/payments', labelKey: 'nav.payments', icon: CreditCard },
      { to: '/payments/bank-slip/upload', labelKey: 'nav.bankSlip', icon: Upload },
    ],
  },
  {
    title: 'Agency Network',
    audience: 'AGENT',
    entries: [
      { to: '/', labelKey: 'nav.dashboard', icon: Home },
      { to: '/commissions', labelKey: 'nav.commissions', icon: Wallet, minTier: TierLevel.L2 },
      { to: '/withdrawals', labelKey: 'nav.withdrawals', icon: PiggyBank, minTier: TierLevel.L2 },
      { to: '/team', labelKey: 'nav.team', icon: Users, minTier: TierLevel.L2 },
    ],
  },
  {
    title: 'Account & Support',
    audience: 'ALL',
    entries: [
      { to: '/notices', labelKey: 'nav.notices', icon: Bell },
      { to: '/profile', labelKey: 'nav.profile', icon: User },
    ],
  },
];

/** W02 — World-Class Slick & Compact Sidebar with Unique Docked Action Micro-Card */
export function Sidebar({ tier }: { tier: TierLevel }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const notices = useNotices();
  const stats = useDashboardStats();
  const { locked } = usePaymentLock();

  const rank = TIER_RANK_MAP[tier] ?? 1;
  const isAgent = rank >= 2;
  const unreadCount = notices.data?.length ?? 0;

  const commissionBalance = resolveWallet(stats.data).availableForWithdrawal;
  const monthlyAmount = stats.data?.monthlyAmount;

  return (
    <nav className="sidebar hide-mobile" aria-label="Primary">
      {/* 1. Brand Capsule */}
      <div className="sidebar-brand-capsule">
        <div className="sidebar-brand-icon">
          <Building2 size={16} />
        </div>
        <div className="sidebar-brand-text">
          <span className="sidebar-brand-title">Sristi Green Valley Ltd</span>
          <span className="sidebar-brand-status">
            <span className="pulse-dot" /> Financial Portal
          </span>
        </div>
      </div>

      {/* 2. Compact User Identification */}
      {user && (
        <div className="sidebar-user-strip">
          <div className="sidebar-user-avatar">
            {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user.fullName}</span>
            <span className="sidebar-user-meta">
              {TIER_DESIGNATION_MAP[user.tier] ?? user.tier}
            </span>
          </div>
        </div>
      )}

      {/* 3. Sectioned Navigation */}
      <div className="sidebar-nav-groups">
        {SECTIONS.map((section) => {
          if (section.audience !== 'ALL' && section.audience !== (isAgent ? 'AGENT' : 'SUBSCRIBER')) {
            return null;
          }

          const visibleEntries = section.entries.filter(
            (e) => !e.minTier || rank >= (TIER_RANK_MAP[e.minTier] ?? 99),
          );

          if (visibleEntries.length === 0) return null;

          return (
            <div className="sidebar-section" key={section.title}>
              <span className="sidebar-section-label">{section.title}</span>
              {visibleEntries.map(({ to, labelKey, icon: Icon }) => {
                const isNotice = to === '/notices';
                if (to === '/payments/bank-slip/upload' && locked) {
                  return (
                    <span key={to} className="sidebar-item pay-locked" role="link" aria-disabled="true" title={t('payments.locked.short')} data-testid="sidebar-pay-locked">
                      <span className="sidebar-item-icon">
                        <Icon size={16} aria-hidden />
                      </span>
                      <span>{t(labelKey)}</span>
                    </span>
                  );
                }
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
                    data-testid={`sidebar-${to === '/' ? 'home' : to.replaceAll('/', '-')}`}
                  >
                    <span className="sidebar-item-icon">
                      <Icon size={16} aria-hidden />
                    </span>
                    <span>{t(labelKey)}</span>
                    {isNotice && unreadCount > 0 && (
                      <span className="sidebar-item-badge">{unreadCount}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* 4. Unique Docked Action Micro-Card */}
      {isAgent ? (
        <div className="sidebar-dock-card">
          <div className="sidebar-dock-head">
            <span>{t('dashboard.agencyWalletDock')}</span>
            <Zap size={12} color="#0d7c3d" />
          </div>
          <div className="sidebar-dock-val">
            ৳ {(commissionBalance ?? 0).toLocaleString('en-IN')}
          </div>
          <div className="sidebar-dock-sub">{t('dashboard.availableForWithdrawal')}</div>
          <Link to="/withdrawals" className="sidebar-dock-btn">
            Withdraw Funds <ArrowRight size={11} />
          </Link>
        </div>
      ) : (
        <div className="sidebar-dock-card">
          <div className="sidebar-dock-head">
            <span>Next Due (EMI)</span>
            <Zap size={12} color="#0d7c3d" />
          </div>
          <div className="sidebar-dock-val">
            {monthlyAmount != null ? `৳ ${monthlyAmount.toLocaleString('en-IN')}` : '—'}
          </div>
          <div className="sidebar-dock-sub">Fixed Monthly Due</div>
          <PayLink to={BKASH_ENABLED ? '/payments/bkash' : PAY_FALLBACK_PATH} className="sidebar-dock-btn">
            {BKASH_ENABLED ? '⚡ Pay with bKash' : 'Upload Bank Slip'} <ArrowRight size={11} />
          </PayLink>
        </div>
      )}
    </nav>
  );
}

export default Sidebar;
